import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Container,
    useTheme,
    alpha,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar
} from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import AdminLayout from './components/AdminLayout';
import UserTable from './components/UserTable';
import UserFilters from './components/UserFilters';
import { getUsers, deleteUser, UserListParams } from '../../services/userService';
import { User, UserRole } from '../../types/auth';
import UserFormDialog from '../../components/admin/UserFormDialog';
import { SelectChangeEvent } from '@mui/material';

interface NewUserManagementPageProps {
    initialIsAddMode?: boolean;
}

const NewUserManagementPage: React.FC<NewUserManagementPageProps> = ({ initialIsAddMode = false }) => {
    const theme = useTheme();
    const location = useLocation();

    // State
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRole, setSelectedRole] = useState<UserRole | 'all'>('all');

    // Dialog States
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    // Notification State
    const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: UserListParams = {
                page: page + 1,
                limit: rowsPerPage,
                search: searchQuery || undefined
            };

            if (selectedRole !== 'all') {
                params.role = selectedRole;
            }

            const response = await getUsers(params);
            setUsers(response.users);
            setTotalUsers(response.total);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to fetch users. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, searchQuery, selectedRole]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Handle initial actions from location state or props
    useEffect(() => {
        const state = location.state as { userId?: number; isAddMode?: boolean } | null;
        if (state?.isAddMode || initialIsAddMode) {
            handleOpenCreateDialog();
        }
    }, [location.state, initialIsAddMode]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        setPage(0);
    };

    const handleRoleChange = (event: SelectChangeEvent<UserRole | 'all'>) => {
        setSelectedRole(event.target.value as UserRole | 'all');
        setPage(0);
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog Handlers
    const handleOpenCreateDialog = () => {
        setSelectedUser(null);
        setIsEditMode(false);
        setDialogOpen(true);
    };

    const handleOpenEditDialog = (user: User) => {
        setSelectedUser(user);
        setIsEditMode(true);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedUser(null);
    };

    const handleUserFormSuccess = () => {
        fetchUsers();
        handleCloseDialog();
        setSnackbar({
            open: true,
            message: `User ${isEditMode ? 'updated' : 'created'} successfully`,
            severity: 'success'
        });
    };

    const handleOpenDeleteDialog = (user: User) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;
        try {
            await deleteUser(userToDelete.id);
            setSnackbar({
                open: true,
                message: 'User deleted successfully',
                severity: 'success'
            });
            fetchUsers();
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to delete user',
                severity: 'error'
            });
        } finally {
            handleCloseDeleteDialog();
        }
    };

    return (
        <AdminLayout>
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            User Management
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Manage students, teachers, and administrators.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={() => fetchUsers()}
                            sx={{ color: 'text.secondary' }}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            onClick={handleOpenCreateDialog}
                            sx={{
                                borderRadius: 3,
                                textTransform: 'none',
                                px: 3,
                                boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`
                            }}
                        >
                            Add User
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                <UserFilters
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                    selectedRole={selectedRole}
                    onRoleChange={handleRoleChange}
                />

                <UserTable
                    users={users}
                    loading={loading}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalUsers={totalUsers}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onEdit={handleOpenEditDialog}
                    onDelete={handleOpenDeleteDialog}
                />

                {/* Dialogs */}
                {dialogOpen && (
                    <UserFormDialog
                        open={dialogOpen}
                        onClose={handleCloseDialog}
                        user={selectedUser}
                        isEditMode={isEditMode}
                        onSuccess={handleUserFormSuccess}
                    />
                )}

                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleCloseDeleteDialog}
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong>?
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={handleCloseDeleteDialog} sx={{ borderRadius: 2 }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            color="error"
                            variant="contained"
                            sx={{ borderRadius: 2 }}
                        >
                            Delete User
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        sx={{ width: '100%', borderRadius: 2 }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </AdminLayout>
    );
};

export default NewUserManagementPage;
